import React, { useEffect, useState } from "react";
import Modal from "../../common/Modal";
import FormInput from "../../common/FormInput";
import { useSubmission } from "../../../hooks/useCallApi";
import toast from "react-hot-toast";

const EditCategoryModal = ({ isOpen, onClose, category, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    priority: "",
    status: "",
  });

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || "",
        description: category.description || "",
        priority: category.priority || "",
        status: category.status || "",
      });
    }
  }, [category]);

  const { mutate: updateCategory, isLoading: isUpdating } = useSubmission(
    ['update-category', category?.id],
    (api, data) => {
      console.log('Updating category:', { id: category.id, data });
      return api.admin.complaints.updateCategory(category.id, data);
    },
    {
      onSuccess: (response) => {
        console.log('Update success:', response);
        toast.success('Category updated successfully!');
        
        if (onUpdate) {
          onUpdate(response?.data);
        }
        
        onClose();
      },
      onError: (error) => {
        console.error('Update error:', error);
        toast.error(error?.response?.data?.message || 'Failed to update category');
      }
    }
  );

  const handleUpdate = () => {
    if (!formData.name || !formData.description || !formData.priority || !formData.status) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!category?.id) {
      toast.error('Category ID is missing');
      return;
    }

    const payload = {
      name: formData.name,
      description: formData.description,
      priority: formData.priority,
      status: formData.status
    };

    console.log('Submitting update:', payload);
    updateCategory(payload);
  };

  const editModalFooter = (
    <div className="flex justify-end gap-2">
      <button
        onClick={onClose}
        disabled={isUpdating}
        className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Cancel
      </button>
      <button
        onClick={handleUpdate}
        disabled={isUpdating}
        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isUpdating ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Updating...
          </>
        ) : (
          'Update Changes'
        )}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title="Edit Category"
      footer={editModalFooter}
    >
      <div className="space-y-4 py-2">
        {/* Name */}
        <FormInput
          label="Category Name"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Enter category name"
          disabled={isUpdating}
        />

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows="3"
            placeholder="Enter brief description here"
            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 transition-all duration-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={isUpdating}
          />
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priority Level <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.priority}
            onChange={(e) =>
              setFormData({ ...formData, priority: e.target.value })
            }
            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 transition-all duration-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={isUpdating}
          >
            <option value="">Select priority</option>
            <option value="High">High</option>
            <option value="Normal">Normal</option>
            <option value="Low">Low</option>
          </select>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 transition-all duration-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={isUpdating}
          >
            <option value="">Select status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>
    </Modal>
  );
};

export default EditCategoryModal;